import React from 'react';

import { observer } from 'mobx-react';

import Box from '@mui/material/Box';

import { CenteredCircularProgress } from 'components/Progress';

import { SystemError } from 'containers/Error/Pages';

import HistoryFeed from 'components/HistoryFeed';

import API from 'utils/api';

import { EntityFieldType, EntityHistoryResponseType } from 'utils/api/modules/entities/entities.types';

import { CREATED_AT_KEY, IMPORTED_KEY, AMENDMENT_KEY } from 'stores/EntityDetails/EntityDetails';

import { EntitiesStores } from '../types';
import { Mapping } from './utils';
import AmendmentRow from './AmendmentRow';
import CreatedAtRow from './CreatedAtRow';
import HistoryRow from './HistoryRow';
import ImportedRow from './ImportedRow';

export type HistoryEntryMapping = Record<
  string,
  {
    Row: React.ComponentType<{ item: EntityHistoryResponseType }>;
    rowKey?: (item: EntityHistoryResponseType) => string;
  }
>;

export const historyRowKey = (item: EntityHistoryResponseType, mapping: HistoryEntryMapping = {}): string => {
  const { entityField, createdAt } = item;
  const base = `${entityField.key}-${entityField.uuid}-${createdAt}`;
  const rowKey = mapping[entityField.key]?.rowKey;
  return rowKey ? `${base}-${rowKey(item)}` : base;
};

const EntityHistoryTab = ({
  store,
  mapping = {},
}: {
  store: EntitiesStores;
  mapping?: HistoryEntryMapping;
}): React.ReactElement => {
  const { history } = store;

  const [fields, setFields] = React.useState<EntityFieldType[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const fetchFields = async (): Promise<void> => {
      setLoading(true);
      await store.loadHistory(store.details?.uuid as string, true);

      const {
        data: { results },
      } = await API.entities.loadEntityFields(store.entityType, {
        pagination: 'false',
        include_deleted: true,
      })();

      setFields(results);
      setLoading(false);
    };

    fetchFields();

    return (): void => {
      setFields([]);
    };
  }, []);

  if ((store.loadingHistory && !store.initialHistory?.length) || loading) {
    return <CenteredCircularProgress CircularProgressProps={{ style: { margin: '20px 0' } }} />;
  }

  if (!history) return <SystemError />;

  const fieldsByUuid = new Map(fields.map((field) => [field.uuid, field]));

  const renderEntry = (item: EntityHistoryResponseType): React.ReactNode => {
    const { entityField, createdBy, current } = item;

    if (entityField.key === CREATED_AT_KEY) return <CreatedAtRow item={item} entityType={store.entityType} />;
    if (entityField.key === IMPORTED_KEY) return <ImportedRow item={item} />;
    if (entityField.key === AMENDMENT_KEY) return <AmendmentRow item={item} />;
    const Row = mapping[entityField.key]?.Row;
    if (Row) return <Row item={item} />;

    const field = entityField.uuid ? fieldsByUuid.get(entityField.uuid) : undefined;
    if (!field) {
      return <p>No field found for {entityField.key}</p>;
    }

    if (!field.protectorType) {
      return <p>No protector type found for {entityField.key}</p>;
    }

    return <HistoryRow item={item}>{Mapping(field, createdBy, current)}</HistoryRow>;
  };

  return (
    <Box sx={{ margin: '16px 20px' }}>
      <HistoryFeed
        days={Object.entries(history).sort(([a], [b]) => b.localeCompare(a))}
        getKey={(item) => historyRowKey(item, mapping)}
        renderEntry={renderEntry}
        hasMore={store.historyHasMore}
        loadingMore={store.loadingHistory}
        onLoadMore={() => store.loadHistory(store.details?.uuid as string)}
      />
    </Box>
  );
};

export default observer(EntityHistoryTab);
